import React, { useState, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import Select from "../ReactSelect";
import { AiOutlineSearch, AiOutlineCloseSquare } from "react-icons/ai";
import { MdKeyboardArrowUp, MdKeyboardArrowDown } from "react-icons/md";

import { storeReportType, storeSearchQuery } from "../../actions/reports";
import reportTypes from "./reportTypes";
import Loading from "../Loading";
/*
 * Dropdown for selecting the type of report.
 *
 * @param {Function} setSelectedReportType - Update the store with the selected Report Type.
 * @param {Function} setSearchQuery - Update the store with the search query so that <SupporterTable /> can access it.
 */
const renderDropdownArrow = ({ isOpen }) => {
  const MdArrow = isOpen ? MdKeyboardArrowUp : MdKeyboardArrowDown;
  const blueClass = isOpen ? "" : " blue";
  return (
    <MdArrow className={`Select__arrow position__center--right${blueClass}`} />
  );
};

renderDropdownArrow.defaultProps = {
  isOpen: false,
};

renderDropdownArrow.propTypes = {
  isOpen: PropTypes.bool,
};

const options = [
  {
    value: reportTypes.regular,
    label: reportTypes.regular,
  },
  {
    value: reportTypes.changes,
    label: reportTypes.changes,
  },
  {
    value: reportTypes.received,
    label: reportTypes.received,
  },
];

const SupporterTableFilters = ({
  reportType,
  setSelectedReportType,
  setSearchQuery,
  loading,
  setLoading,
  detailsLoading,
  onReportFilterChange,
}) => {
  const [searchValue, setSearchValue] = useState("");

  const handleClearSearch = useCallback(() => {
    setSearchValue("");
    setSearchQuery("");
    onReportFilterChange({ search: "" });
  }, [setSearchQuery, setSearchValue, onReportFilterChange]);

  const handleSelectChange = useCallback(
    ({ value }) => {
      handleClearSearch();
      if (value !== reportType) {
        setSelectedReportType(value);
        setLoading(true);
        onReportFilterChange({ report: value });
      }
    },
    [
      handleClearSearch,
      onReportFilterChange,
      reportType,
      setSelectedReportType,
      setLoading,
    ]
  );

  const handleSearchSubmit = useCallback(
    (e) => {
      setLoading(true);
      setSearchQuery(searchValue);
      onReportFilterChange({ search: searchValue });
    },
    [searchValue, onReportFilterChange, setLoading, setSearchQuery]
  );

  const handleKeyboardSubmit = (e) => {
    if (e.keyCode === 13 || e.key === "Enter") {
      handleSearchSubmit(e);
    }
  };

  const selected =
    useMemo(
      () => options.find((option) => option.value === reportType),
      [reportType]
    ) || options[0];

  const handleSearchInputChange = ({ target }) => setSearchValue(target.value);

  return (
    <div className="staff-report-filters">
      {loading && !detailsLoading && <Loading />}
      <label htmlFor="reportType">
        <span className="label-text">Select Report Type:</span>
        <Select
          className="staff-report-filters__type"
          name="reportType"
          onChange={handleSelectChange}
          value={selected}
          options={options}
          arrowRenderer={renderDropdownArrow}
        />
      </label>
      {reportType !== reportTypes.changes && (
        <div className="staff-report-filters__search">
          <input
            placeholder="Search for a financial partner"
            className={searchValue ? "active" : ""}
            value={searchValue}
            onChange={handleSearchInputChange}
            onKeyDown={handleKeyboardSubmit}
          />
          {searchValue && (
            <button
              type="button"
              className="staff-report-filters__reset"
              onClick={handleClearSearch}
            >
              <AiOutlineCloseSquare />
            </button>
          )}
          <button
            className="staff-report-filters__submit"
            onClick={handleSearchSubmit}
            type="submit"
          >
            <AiOutlineSearch />
          </button>
        </div>
      )}
    </div>
  );
};

SupporterTableFilters.defaultProps = {
  loading: true,
  detailsLoading: true,
};

SupporterTableFilters.propTypes = {
  setSelectedReportType: PropTypes.func.isRequired,
  setSearchQuery: PropTypes.func.isRequired,
  setLoading: PropTypes.func.isRequired,
  reportType: PropTypes.string.isRequired,
  loading: PropTypes.bool,
  detailsLoading: PropTypes.bool,
  onReportFilterChange: PropTypes.func.isRequired,
};

const mapDispatchToProps = (dispatch) => ({
  setSelectedReportType: (reportType) => dispatch(storeReportType(reportType)),
  setSearchQuery: (query) => dispatch(storeSearchQuery(query)),
  setLoading: (loading) => dispatch({ type: "SET_TABLE_LOADING", loading }),
});

const mapStateToProps = (state) => ({
  reportType: state.reports.type,
  loading: state.reports.table.loading,
  detailsLoading: state.reports.details.loading,
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(SupporterTableFilters);
